import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown, LuListFilter } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { GLASS_FIELD } from '../../../components/ui/glass';
import { useTranslation } from '../../../i18n/LanguageContext';
import { COLUMN_KEYS, EMPLOYEE_COLUMNS, columnLabel, getFilterState, optionLabel } from '../employeeColumns';

// Status has its own switch in the toolbar
const FILTER_KEYS = COLUMN_KEYS.filter((key) => key !== 'Active');

const selectClass = `${GLASS_FIELD} tw-cursor-pointer tw-appearance-none tw-pr-9 [&>option]:tw-bg-white [&>option]:tw-text-slate-800 dark:[&>option]:tw-bg-[#1f232b] dark:[&>option]:tw-text-slate-100`;

const FilterField = ({ columnKey, value, onChange }) => {
  const { t } = useTranslation();
  const column = EMPLOYEE_COLUMNS[columnKey];
  const id = `employee-filter-${columnKey}`;
  const current = value === undefined || value === null ? '' : String(value);
  const state = getFilterState(columnKey, value);

  return (
    <div className="tw-space-y-1.5">
      <label htmlFor={id} className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-1 tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">
        <span className="tw-truncate">{columnLabel(t, columnKey)}</span>
        {state === 'pending' && <span className="tw-shrink-0 tw-font-medium tw-text-amber-600 dark:tw-text-amber-300">{t('employees.toolbar.minChars')}</span>}
        {state === 'applied' && <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full tw-bg-teal-500" />}
      </label>
      {column.type === 'select' ? (
        <span className="tw-relative tw-block">
          <select id={id} value={current} onChange={(event) => onChange(columnKey, event.target.value)} className={selectClass}>
            <option value="">{t('employees.filter.all')}</option>
            {column.options.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>{optionLabel(t, option)}</option>
            ))}
          </select>
          <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
        </span>
      ) : (
        <input
          id={id}
          type={column.type === 'date' ? 'date' : 'text'}
          value={current}
          onChange={(event) => onChange(columnKey, event.target.value)}
          placeholder={column.type === 'date' ? undefined : t('employees.filter.placeholder')}
          enterKeyHint="done"
          className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
        />
      )}
    </div>
  );
};

FilterField.propTypes = {
  columnKey: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
};

/**
 * Column filters where the table headers aren't shown (the card list on
 * narrow screens). A bottom sheet on phones; results update as you type.
 */
const FilterSheet = ({ isOpen, onClose, filters, onFilterChange, onClearFilters, resultCount }) => {
  const { t, language } = useTranslation();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('employees.toolbar.filters')}
      subtitle={t('employees.filterSheet.subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuListFilter aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClearFilters}>{t('employees.toolbar.clearAll')}</Button>
          <Button variant="primary" onClick={onClose} className="max-sm:tw-flex-1">
            {t('employees.filterSheet.show', { count: resultCount })}
          </Button>
        </>
      )}
    >
      <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
        {FILTER_KEYS.map((key) => (
          <FilterField key={key} columnKey={key} value={filters[key]} onChange={onFilterChange} />
        ))}
      </div>
    </Modal>
  );
};

FilterSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  resultCount: PropTypes.number.isRequired,
};

export default FilterSheet;
